import { useGetAlbumVideo } from '@vimeo/gctl-api/hooks/useAlbumVideo';
import { useViewer } from '@vimeo/viewer';

import { useAlbumStore } from '../state/albumStore';
import { VIDEOS_API_VERSION } from '../utils/constants';
import { fromISO } from '../utils/helper';

// Globals
const LONG_POLL_DELAY = 30000; // 30 seconds
const SHORT_POLL_DELAY = 8000; // 8 seconds

const upcomingStatuses = ['unavailable', 'pending', 'ready'];
const pollingStatuses = ['streaming', ...upcomingStatuses];

const getPollDelay = (scheduledStartTime?: string | null) => {
  if (!scheduledStartTime) return LONG_POLL_DELAY;
  const hoursUntilStart = fromISO(scheduledStartTime).diffNow('hours').hours;
  return hoursUntilStart > 12 ? LONG_POLL_DELAY : SHORT_POLL_DELAY;
};

/**
 * Custom hook to fetch and manage live video data.
 *
 * This hook returns:
 *  1. The current live video's status booleans: `isUpcoming`, `isStreaming`, `isArchived`.
 *  2. A DateTime object representing the scheduled start time of upcoming live video, returns undefined if the video is streaming or archived.
 */
export const useLiveShowcaseData = (videoId?: string) => {
  const viewer = useViewer();
  const { albumId, password } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
  }));

  const { data } = useGetAlbumVideo(
    () => {
      if (!videoId || !viewer) return null;

      return {
        where: { albumId, videoId: videoId as unknown as number },
        select: ['live.archivedTime', 'live.scheduledStartTime', 'live.status'],
        query: {
          ...(password && { password }),
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      refreshInterval: (data) => {
        // Poll the live status at a specified interval if the current status is upcoming or streaming
        const shouldPollStatus = pollingStatuses.some((status) => status === data?.live?.status);
        return shouldPollStatus ? getPollDelay(data?.live?.scheduledStartTime) : 0;
      },
      refreshWhenHidden: false,
      revalidateOnFocus: false,
    },
  );

  const liveVideoData = data?.live;
  const liveStatus = liveVideoData?.status;
  const startTime = liveVideoData?.scheduledStartTime;
  const archivedTime = liveVideoData?.archivedTime;

  /**
   * Live statuses
   */
  const isArchived = liveStatus === 'done' && liveVideoData?.archivedTime;
  const isStreaming = liveStatus === 'streaming';
  const isUpcoming = upcomingStatuses.some((status) => liveStatus === status);

  return {
    isArchived,
    isStreaming,
    isUpcoming,
    startTime,
    archivedTime,
  };
};
